import {
  BoxGeometry,
  Color,
  Group,
  Mesh,
  MeshStandardMaterial,
  PointLight,
  type Scene,
} from 'three';
import type { InventorySlot } from './catalog';
import { ITEM_CATALOG, ItemId } from './catalog';
import type { Vector3D } from '../types';

export interface LootDrop {
  id: string;
  position: Vector3D;
  /** Materials lost on death. */
  contents: InventorySlot[];
  /** World generation id — drops from old caves are invalid after regen. */
  runId: number;
  /** Stage where the player died. */
  stage: number;
  createdAt: number;
  mesh: Group;
}

const PICKUP_RADIUS = 1.55;
const MAX_DROPS = 12;

/**
 * Death loot bags: snapshot the run bag at death, render a gravestone,
 * restore contents when the player walks over them.
 */
export class LootDropManager {
  private readonly drops: LootDrop[] = [];
  private readonly scene: Scene;
  private readonly stoneGeo = new BoxGeometry(0.55, 0.7, 0.22);
  private readonly baseGeo = new BoxGeometry(0.7, 0.12, 0.4);
  private bobPhase = 0;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  list(): ReadonlyArray<LootDrop> {
    return this.drops;
  }

  /**
   * Spawn a recoverable loot bag. Empty contents → no drop.
   * Returns null if there was nothing to drop.
   */
  spawn(
    id: string,
    position: Vector3D,
    contents: InventorySlot[],
    runId: number,
    stage: number,
  ): LootDrop | null {
    const filtered = contents.filter((s) => s.amount > 0);
    if (filtered.length === 0) return null;

    // Cap history — oldest bags despawn (loot lost forever).
    while (this.drops.length >= MAX_DROPS) {
      this.destroyDrop(this.drops[0]!);
    }

    const mesh = this.createGravestoneMesh(filtered);
    mesh.position.set(position.x, position.y, position.z);
    this.scene.add(mesh);

    const drop: LootDrop = {
      id,
      position: { ...position },
      contents: filtered.map((s) => ({ itemId: s.itemId, amount: s.amount })),
      runId,
      stage,
      createdAt: performance.now(),
      mesh,
    };
    this.drops.push(drop);
    return drop;
  }

  /** Remove every drop (call when caves regenerate). */
  clearAll(): void {
    for (let i = this.drops.length - 1; i >= 0; i--) {
      this.destroyDrop(this.drops[i]!);
    }
  }

  /** Remove drops that belong to a previous world generation. */
  clearStale(currentRunId: number): void {
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      if (d.runId !== currentRunId) this.destroyDrop(d);
    }
  }

  /**
   * If the player is within pickup range of a valid drop, recover it.
   * Returns the drop id + contents, or null.
   */
  tryPickup(
    playerPos: Vector3D,
    currentRunId: number,
  ): { dropId: string; contents: InventorySlot[] } | null {
    let best: LootDrop | null = null;
    let bestDist = PICKUP_RADIUS;

    for (const drop of this.drops) {
      if (drop.runId !== currentRunId) continue;
      const dx = drop.position.x - playerPos.x;
      const dy = drop.position.y - playerPos.y;
      const dz = drop.position.z - playerPos.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist <= bestDist) {
        bestDist = dist;
        best = drop;
      }
    }

    if (!best) return null;
    const contents = best.contents;
    const dropId = best.id;
    this.destroyDrop(best);
    return { dropId, contents };
  }

  /** Idle bob / glow pulse for visibility. */
  update(dt: number): void {
    this.bobPhase += dt * 2.4;
    for (const drop of this.drops) {
      const bob = Math.sin(this.bobPhase + drop.createdAt * 0.001) * 0.06;
      drop.mesh.position.y = drop.position.y + bob;
      drop.mesh.rotation.y += dt * 0.6;
    }
  }

  summarize(contents: readonly InventorySlot[]): string {
    return contents
      .map((s) => `${ITEM_CATALOG[s.itemId].name}×${s.amount}`)
      .join(', ');
  }

  totalItems(contents: readonly InventorySlot[]): number {
    return contents.reduce((n, s) => n + s.amount, 0);
  }

  dispose(): void {
    this.clearAll();
    this.stoneGeo.dispose();
    this.baseGeo.dispose();
  }

  private destroyDrop(drop: LootDrop): void {
    const idx = this.drops.indexOf(drop);
    if (idx >= 0) this.drops.splice(idx, 1);
    drop.mesh.traverse((obj) => {
      const mesh = obj as Mesh;
      if (mesh.isMesh) {
        const mat = mesh.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else (mat as MeshStandardMaterial).dispose?.();
      }
      if ((obj as PointLight).isLight) {
        // PointLight has no geometry
      }
    });
    drop.mesh.removeFromParent();
  }

  private createGravestoneMesh(contents: InventorySlot[]): Group {
    const group = new Group();

    const hasGold = contents.some((s) => s.itemId === ItemId.GoldOre);
    const accent = hasGold ? 0xf0c43a : 0x8fa0b8;

    const stoneMat = new MeshStandardMaterial({
      color: 0x4a5560,
      roughness: 0.85,
      metalness: 0.15,
      emissive: new Color(accent),
      emissiveIntensity: 0.25,
    });
    const baseMat = new MeshStandardMaterial({
      color: 0x2a3038,
      roughness: 0.9,
      metalness: 0.05,
      emissive: new Color(accent),
      emissiveIntensity: 0.15,
    });

    const stone = new Mesh(this.stoneGeo, stoneMat);
    stone.position.y = 0.45;
    const base = new Mesh(this.baseGeo, baseMat);
    base.position.y = 0.06;

    const light = new PointLight(accent, 0.85, 6, 2);
    light.position.y = 0.6;

    group.add(base, stone, light);
    return group;
  }
}
